import { API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { ERROR_STATUS_CODES } from "@/constants/error";
import { AppRoutes } from "@/routes/config";
import { TApiError } from "@/types/api";
import { logout } from "@/utils/api";
import { toast } from "@/utils/toast";
import { AxiosError } from "axios";
import Cookies from "js-cookie";
import { FieldErrors, FieldValues, Path, UseFormReturn } from "react-hook-form";
import { ValidationError } from "yup";

/**
 * Checks if the error is an API error.
 *
 * @param {unknown} error - The error to check.
 * @returns {error is TApiError} - True if the error is an API error, false otherwise.
 */
export const isApiError = (error: unknown): error is TApiError => {
  if (error && typeof error === "object" && "message" in error) return true;
  return false;
};

/**
 * Checks if the error is a validation error.
 *
 * @param {unknown} error - The error to check.
 * @returns {error is ValidationError} - True if the error is a validation error, false otherwise.
 */
export const isValidatorError = (error: unknown): error is ValidationError => {
  if (error && typeof error === "object" && "path" in error && "message" in error) return true;
  return false;
};

/**
 * Sets form error messages based on the error type.
 *
 * @template T - The type of form field values.
 * @param {UseFormReturn<T, object, undefined>} methods - The form methods from react-hook-form.
 * @param {unknown} error - The error to handle.
 */
export const setErrorForm = <T extends FieldValues>(methods: UseFormReturn<T, object, T>, error: unknown) => {
  const { setError } = methods;
  if (isValidatorError(error)) {
    setError(error.path as Path<T>, { message: error.message });
    return;
  }
  if (isApiError(error)) {
    if (error.error && Array.isArray(error.error)) {
      error.error.forEach(({ property, message }) => {
        setError(property as Path<T>, { message });
      });
      return;
    }
  }
};

/**
 * Displays an error message using the toast utility.
 *
 * @param {unknown} error - The error to handle.
 */
export const showError = (error: unknown) => {
  if (isApiError(error) && (error.message || error.error)) {
    if (error.error) {
      if (Array.isArray(error.error)) {
        toast.error(error.error.map((e) => e.message).join("\n"));
      } else if (typeof error.error === "string") {
        toast.error(error.error);
      } else {
        toast.error(error.message);
      }
    } else if (error.message) {
      toast.error(error.message);
    }
  } else {
    toast.error(API_UNKNOWN_ERROR_MESSAGE);
  }
};

/**
 * Handles API errors based on error code and redirects if necessary.
 *
 * @param {AxiosError} error - The error to handle.
 */
export const handleApiErrorWithCode = (error: AxiosError) => {
  if (error.code === "ERR_NETWORK") {
    setTimeout(() => window.location.replace(AppRoutes.serverErrorPage), 1000);
    return;
  }

  if (error.config?.method === "get") {
    if (isApiError(error.response?.data)) {
      const { code, message } = error.response.data;
      if (code !== ERROR_STATUS_CODES.ZIP_CODE_ERROR && code !== ERROR_STATUS_CODES.ZIP_CODE_INVALID) {
        toast.error(message);
      }
      switch (code) {
        // case ERROR_STATUS_CODES.INFORMATION_NOT_EXISTS:
        //   window.location.replace(AppRoutes.information);
        //   return;
        // case ERROR_STATUS_CODES.COMPANY_NOT_EXISTS:
        //   window.location.replace(AppRoutes.companyList);
        //   return;
        // case ERROR_STATUS_CODES.COMPANY_BRANCH_NOT_EXISTS:
        //   window.location.replace(AppRoutes.companyBranchList);
        //   return;
        // case ERROR_STATUS_CODES.TALENT_NOT_EXISTS:
        //   window.location.replace(AppRoutes.talentList);
        //   return;
        // case ERROR_STATUS_CODES.DOCUMENT_NOT_EXISTS:
        //   window.location.replace(AppRoutes.regularHistory);
        //   return;
        // case ERROR_STATUS_CODES.ACCEPTANCE_ACTIVITY_DOCUMENT_NOT_EXISTS:
        //   window.location.replace(AppRoutes.regularHistory);
        //   return;
        // case ERROR_STATUS_CODES.ACCEPTANCE_ACTIVITY_FILE_NOT_EXISTS:
        //   window.location.replace(AppRoutes.regularHistory);
        //   return;
        // case ERROR_STATUS_CODES.LIFE_SUPPORT_DOCUMENT_NOT_EXISTS:
        //   window.location.replace(AppRoutes.regularHistory);
        //   return;
        case ERROR_STATUS_CODES.MAINTENANCE_UNUSABLE:
          window.location.replace(AppRoutes.maintenance);
          return;
        case ERROR_STATUS_CODES.MAINTENANCE_READABLE:
          window.location.replace(AppRoutes.home);
          return;
      }
    }
  }

  if (error.response?.status === 403 && isApiError(error.response?.data)) {
    const { code } = error.response.data;
    switch (code) {
      case ERROR_STATUS_CODES.RSO_ACCOUNT_REGISTRATION_CANCELED:
      case ERROR_STATUS_CODES.RSO_USER_NOT_ACTIVE:
        logout();
        if (error.config?.url !== AppRoutes.login) {
          window.location.replace(AppRoutes.login);
        }
        return;
    }
    // Redirect to login for other 403 errors
    if (!window.location.pathname.startsWith(AppRoutes.login)) {
      window.location.replace(AppRoutes.login);
    }
    return;
  }

  if (error.response?.status === 404 && error.config?.method === "get") {
    const errorUrl = error.config?.url || "";
    const isDetailApiCall =
      /\/auth\/company\/\d+/.test(errorUrl) ||
      /\/company\/\d+/.test(errorUrl) ||
      /\/talents\/\d+/.test(errorUrl) ||
      /\/jobs\/\d+/.test(errorUrl) ||
      /\/cases\/\d+/.test(errorUrl) ||
      /\/informations\/\d+/.test(errorUrl) ||
      /\/visa\//.test(errorUrl);

    const currentPath = window.location.pathname;
    const isDetailOrEditPage = currentPath.includes("/detail") || currentPath.includes("/edit");

    if (!isDetailApiCall && !isDetailOrEditPage && !currentPath.startsWith(AppRoutes.pageNotFound)) {
      window.location.replace(AppRoutes.pageNotFound);
    }
  }

  // Uncomment to handle 500 status errors for GET requests
  if (error.response?.status === 500 && error.config?.method === "get") {
    const errorUrl = error.config?.url || "";
    const isDetailApiCall =
      /\/auth\/company\/\d+/.test(errorUrl) ||
      /\/company\/\d+/.test(errorUrl) ||
      /\/talents\/\d+/.test(errorUrl) ||
      /\/jobs\/\d+/.test(errorUrl) ||
      /\/cases\/\d+/.test(errorUrl) ||
      /\/informations\/\d+/.test(errorUrl);

    const currentPath = window.location.pathname;
    const isDetailOrEditPage = currentPath.includes("/detail") || currentPath.includes("/edit");

    if (isDetailOrEditPage && !isDetailApiCall) {
      const pathRouteMap = [
        { path: "/talent", route: AppRoutes.talentList },
        { path: "/companies", route: AppRoutes.companyList },
        { path: "/job", route: AppRoutes.jobList },
        { path: "/cases", route: AppRoutes.caseList },
        { path: "/information", route: AppRoutes.information },
      ];

      const matchedRoute = pathRouteMap.find(({ path }) => currentPath.includes(path));
      if (matchedRoute) {
        window.location.replace(matchedRoute.route);
        return;
      }
    }

    if (!isDetailApiCall && !isDetailOrEditPage && !currentPath.startsWith(AppRoutes.serverErrorPage)) {
      setTimeout(() => window.location.replace(AppRoutes.serverErrorPage), 1000);
    }
  }
};

export const getCustomSentryExceptionError = (error: AxiosError) => {
  const titleSentry = "Irohana Study Error";
  const serverErrorResponse = error.response?.data as { message: string; code: string; error: unknown };
  let name = `[Irohana Study]`;
  if (serverErrorResponse) {
    const customErrorResponseMessage = serverErrorResponse?.error
      ? typeof serverErrorResponse?.error === "string"
        ? serverErrorResponse?.error
        : JSON.stringify(serverErrorResponse?.error)
      : undefined;
    name = name + `: ${serverErrorResponse.message}`;
    const message = `${serverErrorResponse.code}\n${customErrorResponseMessage ?? ""}`;
    const user = { email: Cookies.get("email"), ownerId: Cookies.get("ownerId") };
    const sendError = new Error(`${name} \n ${message} \n ${JSON.stringify(user)}`);
    sendError.name = titleSentry;
    return sendError;
  } else {
    const sendError = new Error(`${name} \n ${error.message}`);
    sendError.name = titleSentry;
    return sendError;
  }
};

export function findIntegerErrorPaths(errors: FieldErrors<FieldValues>, pathPrefix = ""): string[] {
  const paths: string[] = [];

  for (const key in errors) {
    const value = errors[key];

    const currentPath = pathPrefix ? `${pathPrefix}.${key}` : key;

    if (
      typeof value === "object" &&
      value !== null &&
      "type" in value &&
      typeof value.type === "string" &&
      ["integer", "typeError", "type"].includes(value.type)
    ) {
      paths.push(currentPath);
    } else if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          const nestedPaths = findIntegerErrorPaths(item as FieldErrors<FieldValues>, `${currentPath}[${index}]`);
          paths.push(...nestedPaths);
        }
      });
    } else if (typeof value === "object" && value !== null) {
      const nestedPaths = findIntegerErrorPaths(value as FieldErrors<FieldValues>, currentPath);
      paths.push(...nestedPaths);
    }
  }

  return paths;
}

export const triggerIntegerSaveDraftError = async <T extends FieldValues>(methods: UseFormReturn<T, object, undefined>): Promise<boolean> => {
  const errorPaths = findIntegerErrorPaths(methods.formState.errors);

  if (errorPaths.length > 0) {
    const firstPath = errorPaths[0];
    const normalizedPath = firstPath?.replace(/\[(\d+)\]/g, ".$1");

    setTimeout(() => {
      const el = document.querySelector(`[name="${normalizedPath}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return true;
  }

  return false;
};
